import { MilestoneMap1, Objective1 } from '../../../types';
import { DataNode } from '../types';
import { getChildObjectivesForParentObjectiveByOrder } from '../../../utils/objective-utils';

/**
 * Type for objective creation function that different builders can provide
 */
export type ObjectiveCreator = (
  objective: Objective1,
  milestoneMap: MilestoneMap1,
) => DataNode<Objective1>;

/**
 * Shared utility for building objective hierarchy
 *
 * This function handles the common logic for organizing objectives into a hierarchy
 * while allowing different builders to specify how individual objectives should be created.
 *
 * @param milestoneMap - The milestone map containing objective ordering
 * @param objectives - Array of all objectives for this map
 * @param createObjectiveDataNode - Function to create objective nodes with appropriate children
 * @returns Array of DataNode items representing the objective hierarchy
 */
export function buildObjectiveHierarchy(
  milestoneMap: MilestoneMap1,
  objectives: Objective1[],
  createObjectiveDataNode: ObjectiveCreator,
): DataNode<any>[] {
  try {
    const majorObjectiveUuids = milestoneMap.parentObjectiveUuids || [];
    const result: DataNode<any>[] = [];
    const childrenOfMajorObjectives = new Set<string>();

    // Process each major objective and its children using the shared helper
    majorObjectiveUuids.forEach((majorObjUuid) => {
      const parentObjective = objectives.find((obj) => obj.uuid === majorObjUuid);
      if (!parentObjective) return;

      const childObjectives = getChildObjectivesForParentObjectiveByOrder(
        milestoneMap,
        objectives,
        majorObjUuid,
      );

      // Collect child UUIDs while we have them in memory
      childObjectives.forEach((child) => childrenOfMajorObjectives.add(child.uuid));

      const childItems = childObjectives.map((child) =>
        createObjectiveDataNode(child, milestoneMap),
      );

      // Create major objective node
      const majorObjectiveItem: DataNode<Objective1> = {
        id: parentObjective.uuid,
        data: parentObjective,
        // Note: There really shouldn't be parents without children. If there are,
        // the milestone map is probably work in progress, while planning is happening
        // or adjustments are being made. Just display it as a major objective, with nothing under it.
        // If that's real, just surface and visualize reality. It's not a bug. It would be confusing
        // to try to make something up that the user didn't enter.
        children: childObjectives.length > 0 ? childItems : [],
        type: 'MajorObjective',
        metadata: {
          defaultExpanded: true,
        },
      };

      result.push(majorObjectiveItem);
    });

    // Add any objectives that aren't children of major objectives as direct descendants
    const orphanObjectives = objectives.filter(
      (obj) => !majorObjectiveUuids.includes(obj.uuid) && !childrenOfMajorObjectives.has(obj.uuid),
    );

    orphanObjectives.forEach((objective) => {
      result.push(createObjectiveDataNode(objective, milestoneMap));
    });

    return result;
  } catch (error) {
    console.error('❌ Error in buildObjectiveHierarchy:', error);
    return [];
  }
}
